import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { CapgoWatch } from '@capgo/capacitor-watch';
import OCGatewayClient from './OCGatewayClient';
import OCPlatform from './OCPlatform';

/**
 * Provisions the watch app with its gateway credential.
 *
 * The watch is an independent REST client of the gateway (watchOS suspends
 * apps too aggressively for a socket.io session, so it fetches on activation
 * — see the /user routes in onlycat-gateway). All it needs from the phone is
 * the device token and the gateway URL, pushed via the WatchConnectivity
 * application context: the OS keeps the latest context and delivers it even
 * when the watch app is installed long after this ran.
 */

let installed = false;
let lastSentContextKey: string | null = null;

export function installWatchSync() {
    if (installed) {
        return;
    }

    if (Capacitor.getPlatform() !== 'ios') {
        return;
    }

    void syncWatchContext();

    void CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
            void syncWatchContext();
        }
    });

    installed = true;
}

async function syncWatchContext(retryOnFailure = true) {
    try {
        const info = await CapgoWatch.getInfo();

        if (!info.isSupported || !info.isPaired || !info.isWatchAppInstalled) {
            return;
        }

        const context = {
            deviceToken: await OCPlatform.getDeviceToken(),
            apiUrl: OCGatewayClient.apiURL
        };

        const contextKey = JSON.stringify(context);
        if (contextKey === lastSentContextKey) {
            return;
        }

        await CapgoWatch.updateApplicationContext({ context });
        lastSentContextKey = contextKey;

        console.info('(OCWatchSync) Watch application context updated');
    } catch (error) {
        console.warn('(OCWatchSync) Failed to sync watch context', error);

        // At web boot the WCSession may still be activating, which rejects
        // updateApplicationContext; one delayed retry covers first-run
        // provisioning without waiting for the next foreground transition.
        if (retryOnFailure) {
            setTimeout(() => {
                void syncWatchContext(false);
            }, 5000);
        }
    }
}
